import { SdbProject } from "@custom-types/project-types";
import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { fetchMapProjects } from "@store/projects-map/projects-map-thunks";
import { ProjectsMapState } from "@store/projects-map/projects-map-types";
import {
  deleteProject,
  updateProjectDetails,
} from "@store/projects/projects-slice-thunk";
import {
  convertProjectTypesToSdbProject,
  restoreMissingProjectFields,
} from "@store/projects/projects-slice-utils";
import { BaseEntityAdapter } from "@store/store-types";

/** Redux entity adapter for the projects map slice */
export const projectsMapAdapter: BaseEntityAdapter<SdbProject> =
  createEntityAdapter({
    selectId: (project) => project.id,
  });

const initialState: ProjectsMapState = {
  ...projectsMapAdapter.getInitialState(),
  fetchingStatus: "uninitialized",
};

const projectsMapSlice = createSlice({
  name: "projectsMap",
  initialState,
  reducers: {
    /** Adds a newly created project to the map project state */
    addCreatedProject: (state, action: PayloadAction<SdbProject>) => {
      projectsMapAdapter.addOne(state, action.payload);
    },

    /** Resets the projects state */
    resetProjectsMapState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMapProjects.pending, (state) => {
        state.fetchingStatus = "pending";
      })
      .addCase(fetchMapProjects.fulfilled, (state, action) => {
        projectsMapAdapter.setAll(state, action.payload);
        state.fetchingStatus = "succeeded";
      })
      .addCase(fetchMapProjects.rejected, (state) => {
        state.fetchingStatus = "rejected";
      })

      .addCase(updateProjectDetails.fulfilled, (state, action) => {
        const updatedProject = convertProjectTypesToSdbProject(action.payload);
        const existingProject = projectsMapAdapter
          .getSelectors()
          .selectById(state, updatedProject.id);
        // Keep map state in sync with the backend's `requireGpsPosition` filter:
        // a project belongs on the map if it has a GPS position.
        if (existingProject) {
          if (!updatedProject.position) {
            // The location was cleared, so the project should no longer appear on the map.
            projectsMapAdapter.removeOne(state, updatedProject.id);
            return;
          }

          const restoredProject = restoreMissingProjectFields({
            updatedProject,
            existingProject,
          });
          projectsMapAdapter.setOne(state, restoredProject);
          return;
        } else if (updatedProject.position) {
          // The project gained a location and now belongs on the map.
          projectsMapAdapter.addOne(state, updatedProject);
        }
      })

      .addCase(deleteProject.fulfilled, (state, action) => {
        const projectId = action.payload;
        projectsMapAdapter.removeOne(state, projectId);
      });
  },
});

export const { addCreatedProject, resetProjectsMapState } =
  projectsMapSlice.actions;

/** Slice reducer for the projects map */
export const projectsMapReducer = projectsMapSlice.reducer;
